import {createContext} from 'react';

import type {LocaleContextType} from './typedef';
import type {Context} from 'react';

/**
 * Default context value for LocaleContext.
 * Empty object cast to LocaleContextType - components should always be wrapped in a Provider.
 */
const DEFAULT_CONTEXT = {} as LocaleContextType;

/**
 * React Context for providing user locale settings throughout the application.
 *
 * This context provides locale configuration including:
 * - Currency code (e.g., 'USD', 'EUR')
 * - Date format (moment.js compatible format string)
 * - Number formatting (decimal and thousand separators)
 * - Currency symbol position ('LEFT' or 'RIGHT')
 * - Native currency display flag
 *
 * @example
 * // Providing locale context
 * const locale = {
 *   currency: 'EUR',
 *   dateFormat: 'DD-MM-YYYY',
 *   decimalFormat: ',',
 *   thousandFormat: '.',
 *   symbolPosition: 'LEFT',
 *   nativeCurrency: true,
 * };
 *
 * <LocaleContext.Provider value={{locale}}>
 *   <App />
 * </LocaleContext.Provider>
 *
 * @example
 * // Consuming locale context with hooks
 * import {useContext} from 'react';
 * import {LocaleContext} from '@pb/kit-context-locale';
 *
 * function MyComponent() {
 *   const {locale} = useContext(LocaleContext);
 *   return <div>Currency: {locale.currency}</div>;
 * }
 *
 * @example
 * // Consuming locale context with class components
 * import {Component} from 'react';
 * import {LocaleContext} from '@pb/kit-context-locale';
 *
 * class MyComponent extends Component {
 *   static contextType = LocaleContext;
 *   context: LocaleContextType;
 *
 *   render() {
 *     const {locale} = this.context;
 *     return <div>Currency: {locale.currency}</div>;
 *   }
 * }
 */
export const LocaleContext: Context<LocaleContextType> = createContext<LocaleContextType>(
    DEFAULT_CONTEXT,
);

LocaleContext.displayName = 'LocaleContext';
